/**
 * Payment Hook using TanStack Query (V2)
 * Matches the old usePayment.ts implementation exactly for easy migration
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useBasisTheory } from '@basis-theory/basis-theory-react';

import { getBasisTheoryApiKey } from '../../../react/config/payment';
import { useTagadaContext } from '../providers/TagadaProvider';
import { PaymentsResource } from '../../core/resources/payments';
import type {
    Payment,
    PaymentResponse,
    PaymentOptions,
    CardPaymentMethod,
    ApplePayToken,
    GooglePayToken,
    PaymentInstrumentResponse,
    PaymentInstrumentCustomerResponse
} from '../../core/resources/payments';
import { TagadaError, TagadaErrorCode } from '../../core/errors';

import { usePaymentPolling } from './usePaymentPolling';
import { useThreeds } from './useThreeds';
import type { PaymentInstrument } from './useThreeds';
import { getGlobalApiClient } from './useApiQuery';
import { useStoreConfigQuery } from './useStoreConfigQuery';
import { getAssignedPaymentFlowId } from '../../core/funnelClient';
import { usePaymentActionHandler } from './payment-actions/usePaymentActionHandler';
import { useAirwallex3dsReturn } from './payment-redirect/useAirwallex3dsReturn';
import { useGenericPaymentReturn } from './payment-redirect/useGenericPaymentReturn';
import { usePaymentInstruments } from './payment-processing/usePaymentInstruments';
import { usePaymentProcessors } from './payment-processing/usePaymentProcessors';

// Import from old implementation for compatibility
export type {
    Payment as PaymentType,
    PaymentResponse,
    PaymentOptions,
    CardPaymentMethod,
    ApplePayToken,
    PaymentInstrumentResponse,
    PaymentInstrumentCustomerResponse,
    PaymentInstrumentCustomer
} from '../../core/resources/payments';

/**
 * Metadata provided with payment callbacks
 */
export interface PaymentCompletionMetadata {
    /** True if payment completed after external redirect (3DS, PayPal, etc.) */
    isRedirectReturn: boolean;
    /** Order associated with the payment (if available) */
    order?: {
        id: string;
        amount?: number;
        currency?: string;
        [key: string]: unknown;
    };
    /** Checkout session ID (if available) */
    checkoutSessionId?: string;
}

/**
 * Hook-level options for universal payment handling
 */
export interface UsePaymentOptions {
    /**
     * Called when payment completes successfully
     * Works for BOTH immediate success AND post-redirect success (3DS, PayPal, etc.)
     * @param payment - The completed payment
     * @param metadata - Additional context about how payment completed
     */
    onPaymentCompleted?: (payment: Payment, metadata: PaymentCompletionMetadata) => void | Promise<void>;

    /**
     * Called when payment fails
     * Works for BOTH immediate failure AND post-redirect failure
     * @param error - Error message
     * @param metadata - Additional context about the failure
     */
    onPaymentFailed?: (error: string, metadata: PaymentCompletionMetadata) => void | Promise<void>;
}

export interface PaymentHook {
    // Payment processing
    processCardPayment: (
        checkoutSessionId: string,
        cardData: CardPaymentMethod,
        options?: PaymentOptions,
    ) => Promise<PaymentResponse>;
    processApplePayPayment: (
        checkoutSessionId: string,
        applePayToken: ApplePayToken,
        options?: PaymentOptions,
    ) => Promise<PaymentResponse>;
    processGooglePayPayment: (
        checkoutSessionId: string,
        googlePayToken: GooglePayToken,
        options?: PaymentOptions,
    ) => Promise<PaymentResponse>;
    processPaymentWithInstrument: (
        checkoutSessionId: string,
        paymentInstrumentId: string,
        options?: PaymentOptions,
    ) => Promise<PaymentResponse>;
    processApmPayment: (
        checkoutSessionId: string,
        options: {
            processorId: string;
            paymentMethod: string;
            initiatedBy?: 'customer' | 'merchant';
            source?: 'upsell' | 'checkout' | 'offer' | 'missing_club' | 'forced';
            paymentFlowId?: string;
        } & Pick<PaymentOptions, 'onFailure' | 'onPaymentFailed' | 'onSuccess' | 'onPaymentSuccess'>,
    ) => Promise<PaymentResponse>;

    // Payment instrument management
    createCardPaymentInstrument: (cardData: CardPaymentMethod) => Promise<PaymentInstrumentResponse>;
    createApplePayPaymentInstrument: (applePayToken: ApplePayToken) => Promise<PaymentInstrumentResponse>;
    createGooglePayPaymentInstrument: (googlePayToken: GooglePayToken) => Promise<PaymentInstrumentResponse>;
    getCardPaymentInstruments: () => Promise<PaymentInstrumentCustomerResponse>;

    // States
    isLoading: boolean;
    error: TagadaError | null;
    clearError: () => void;

    // Current payment ID
    currentPaymentId: string | null;
}

export function usePaymentQuery(hookOptions?: UsePaymentOptions): PaymentHook {
    const { environment } = useTagadaContext();

    // Get store config to auto-detect 3DS setting from payment flow
    const { storeConfig } = useStoreConfigQuery();

    // Create payments resource client
    const paymentsResource = useMemo(() => {
        try {
            return new PaymentsResource(getGlobalApiClient());
        } catch (error) {
            throw new Error('Failed to initialize payments resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPaymentId, setCurrentPaymentId] = useState<string | null>(null);

    // Store hook-level callbacks in ref to avoid re-renders
    const hookOptionsRef = useRef(hookOptions);
    useEffect(() => {
        hookOptionsRef.current = hookOptions;
    }, [hookOptions]);

    const { startPolling, stopPolling } = usePaymentPolling();
    const { createSession, startChallenge } = useThreeds();

    // Track if we've already processed a redirect return (prevents double-processing)
    const redirectReturnProcessedRef = useRef(false);

    // Get API key from embedded configuration with proper environment detection
    const apiKey = useMemo(() => getBasisTheoryApiKey(), []); // Auto-detects environment

    // Initialize BasisTheory using React wrapper
    const { bt: basisTheory, error: btError } = useBasisTheory(apiKey, {
        elements: false,
    });

    // Handle BasisTheory initialization errors (only log once when state changes)
    useEffect(() => {
        if (btError) {
            setError('Failed to initialize payment processor: ' + btError.message);
        } else if (basisTheory && !error) {
            setError(null); // Clear any previous errors
        }
    }, [basisTheory, btError]); // Removed error from dependency to prevent loops

    // Clean up polling when component unmounts
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    // Use payment action handler hook
    const { handlePaymentAction } = usePaymentActionHandler({
        paymentsResource,
        startChallenge,
        startPolling,
        setIsLoading,
        setError,
        hookOptionsRef,
    });

    // Handle Airwallex 3DS redirect returns
    useAirwallex3dsReturn({
        paymentsResource,
        startPolling,
        handlePaymentAction,
        setIsLoading,
        setError,
        setCurrentPaymentId,
        hookOptionsRef,
        redirectReturnProcessedRef,
    });

    // Handle generic payment redirect returns
    useGenericPaymentReturn({
        startPolling,
        handlePaymentAction,
        setIsLoading,
        setError,
        setCurrentPaymentId,
        hookOptionsRef,
        redirectReturnProcessedRef,
    });

    // Use payment instruments hook
    const paymentInstruments = usePaymentInstruments({
        basisTheory,
        paymentsResource,
        setError,
    });

    const {
        createCardPaymentInstrument,
        createApplePayPaymentInstrument,
        createGooglePayPaymentInstrument,
        getCardPaymentInstruments,
    } = paymentInstruments;

    // Use payment processors hook
    const paymentProcessors = usePaymentProcessors({
        paymentsResource,
        createCardPaymentInstrument,
        createApplePayPaymentInstrument,
        createGooglePayPaymentInstrument,
        createSession,
        handlePaymentAction,
        startPolling,
        setIsLoading,
        setError,
        setCurrentPaymentId,
        hookOptionsRef,
        storeConfig,
    });

    const {
        processCardPayment,
        processApplePayPayment,
        processGooglePayPayment,
        processPaymentWithInstrument,
        processApmPayment,
    } = paymentProcessors;

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const tagadaError = useMemo(
        () => error ? new TagadaError(error, { code: TagadaErrorCode.PAYMENT_FAILED }) : null,
        [error],
    );

    return {
        processCardPayment,
        processApplePayPayment,
        processGooglePayPayment,
        processPaymentWithInstrument,
        processApmPayment,
        createCardPaymentInstrument,
        createApplePayPaymentInstrument,
        createGooglePayPaymentInstrument,
        getCardPaymentInstruments,
        isLoading: isLoading || !basisTheory,
        error: tagadaError,
        clearError,
        currentPaymentId,
    };
}